/**
 * School Mobile App Screenshots API Contract (release lines U8, R20)
 *
 * GET    /v1/schools/:school_id/mobile-app/screenshots
 * POST   /v1/schools/:school_id/mobile-app/screenshots/upload-signature
 * POST   /v1/schools/:school_id/mobile-app/screenshots
 * PUT    /v1/schools/:school_id/mobile-app/screenshots/order
 * DELETE /v1/schools/:school_id/mobile-app/screenshots/:screenshotId
 *
 * A school edits the screenshot sets of ONE line: the newest line its app has a
 * record for that is not superseded or retired (a planned line counts). The
 * school never names the line; the server picks it, and the GET says which it
 * is so the panel can read "Screenshots for version 1.9". Sets on every other
 * line, including the line the app is live on while a newer one is planned, are
 * out of reach of these routes.
 *
 * An upload is verified at Cloudinary before a row is written: the public id
 * must sit in this app's folder for the set (store, line and device class), and
 * the dimensions, mime type, size and version come from Cloudinary.
 */

import { z } from 'zod'
import { SchoolAssetUploadSignatureResponseSchema } from '../../schemas/entities/mobile-app-asset'
import { StoreListingStoreSchema } from '../../schemas/entities/mobile-app-store-listing'
import {
  getStoreScreenshotClass,
  STORE_SCREENSHOT_DEVICE_CLASS_PAIR_RULE,
  StoreScreenshotCommitResponseSchema,
  StoreScreenshotDeviceClassSchema,
  StoreScreenshotOrderResponseSchema,
  StoreScreenshotRemoveResponseSchema,
  StoreScreenshotSetSchema
} from '../../schemas/entities/mobile-app-store-screenshot'

/** The line the school's sets belong to. */
export const SchoolScreenshotLineSchema = z.object({
  /** `AppVersion.publicId`. */
  id: z.string(),
  /** "1.9". */
  name: z.string(),
  runtimeVersion: z.string()
})
export type SchoolScreenshotLine = z.infer<typeof SchoolScreenshotLineSchema>

/**
 * GET response. `line` is null when the app has no line a school may edit
 * (every line it has is superseded or retired), and then `sets` is empty.
 * `sets` holds every catalog device class of both stores, empty ones included.
 */
export const GetSchoolScreenshotsResponseSchema = z.object({
  line: SchoolScreenshotLineSchema.nullable(),
  sets: z.array(StoreScreenshotSetSchema)
})
export type GetSchoolScreenshotsResponse = z.infer<typeof GetSchoolScreenshotsResponseSchema>

const setTarget = { store: StoreListingStoreSchema, deviceClass: StoreScreenshotDeviceClassSchema }

const withClassForStore = <T extends z.ZodType<{ store: string; deviceClass: string }>>(schema: T) =>
  schema.refine(value => getStoreScreenshotClass(value.store, value.deviceClass) !== undefined, {
    message: STORE_SCREENSHOT_DEVICE_CLASS_PAIR_RULE,
    path: ['deviceClass']
  })

/** POST …/upload-signature body. */
export const SchoolScreenshotUploadSignatureRequestSchema = withClassForStore(z.object(setTarget))
export type SchoolScreenshotUploadSignatureRequest = z.infer<typeof SchoolScreenshotUploadSignatureRequestSchema>

/**
 * POST …/upload-signature response: the signed-widget parameters, plus the
 * `tags` string the upload must post verbatim (it is part of the signed
 * payload) and the line runtime the folder was signed for. The client posts
 * these as given; rebuilding them from an earlier read would break the
 * signature if the school's line moved in between.
 */
export const SchoolScreenshotUploadSignatureResponseSchema = SchoolAssetUploadSignatureResponseSchema.extend({
  runtimeVersion: z.string()
})
export type SchoolScreenshotUploadSignatureResponse = z.infer<typeof SchoolScreenshotUploadSignatureResponseSchema>

/**
 * POST …/screenshots body: which set, and the uploaded image's public id. Any
 * other metadata a client sends is dropped; the server reads it from Cloudinary.
 */
export const SchoolScreenshotCommitRequestSchema = withClassForStore(
  z.object({ ...setTarget, cloudinaryPublicId: z.string().min(1) })
)
export type SchoolScreenshotCommitRequest = z.infer<typeof SchoolScreenshotCommitRequestSchema>

/** PUT …/order body: a permutation of the set's ids. */
export const SchoolScreenshotOrderRequestSchema = withClassForStore(
  z.object({ ...setTarget, orderedIds: z.array(z.string().min(1)).min(1) })
)
export type SchoolScreenshotOrderRequest = z.infer<typeof SchoolScreenshotOrderRequestSchema>

/** POST …/screenshots response: the committed image and its set, the staff lanes' shape. */
export const SchoolScreenshotCommitResponseSchema = StoreScreenshotCommitResponseSchema
export type SchoolScreenshotCommitResponse = z.infer<typeof SchoolScreenshotCommitResponseSchema>

/** PUT …/order response. */
export const SchoolScreenshotOrderResponseSchema = StoreScreenshotOrderResponseSchema
export type SchoolScreenshotOrderResponse = z.infer<typeof SchoolScreenshotOrderResponseSchema>

/** DELETE …/:screenshotId response. */
export const SchoolScreenshotRemoveResponseSchema = StoreScreenshotRemoveResponseSchema
export type SchoolScreenshotRemoveResponse = z.infer<typeof SchoolScreenshotRemoveResponseSchema>

/**
 * `NO_SCREENSHOT_LINE`: the app has no line a school may edit.
 * `ASSET_NOT_IN_SCOPE`: the upload is not in this app's folder for the set.
 */
export const SCHOOL_SCREENSHOT_ERROR_CODES = [
  'INVALID_PARAMS',
  'NOT_AUTHORIZED',
  'NO_MOBILE_APP',
  'NO_SCREENSHOT_LINE',
  'NOT_FOUND',
  'ASSET_NOT_IN_SCOPE',
  'ASSET_NOT_FOUND',
  'RATE_LIMITED',
  'VALIDATION_FAILED',
  'CONFLICT',
  'CONFIGURATION_ERROR',
  'INTERNAL_ERROR'
] as const
export type SchoolScreenshotErrorCode = (typeof SCHOOL_SCREENSHOT_ERROR_CODES)[number]
